package com.mgacreative.hotelru.feature.admin.ui

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.Close
import androidx.compose.material.icons.filled.Refresh
import androidx.compose.material.icons.filled.Search
import androidx.compose.material.icons.outlined.AdminPanelSettings
import androidx.compose.material.icons.outlined.Person
import androidx.compose.material.icons.outlined.Shield
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.mgacreative.hotelru.shared.ui.designsystem.components.verticalScrollbar
import com.mgacreative.hotelru.shared.ui.designsystem.i18n.l10n
import org.koin.compose.viewmodel.koinViewModel

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun AdminMembersScreen(
    viewModel: AdminMembersViewModel = koinViewModel(),
    onNavigateBack: () -> Unit = {}
) {
    val state by viewModel.uiState.collectAsState()
    val actionMessage by viewModel.actionMessage.collectAsState()

    val snackbarHostState = remember { SnackbarHostState() }
    var editingMember by remember { mutableStateOf<Member?>(null) }

    LaunchedEffect(actionMessage) {
        actionMessage?.let { msg ->
            snackbarHostState.showSnackbar(msg)
            viewModel.clearActionMessage()
        }
    }

    Scaffold(
        snackbarHost = { SnackbarHost(snackbarHostState) },
        topBar = {
            TopAppBar(
                title = {
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        Icon(
                            imageVector = Icons.Outlined.AdminPanelSettings,
                            contentDescription = null,
                            tint = Color(0xFFD97706),
                            modifier = Modifier.size(24.dp)
                        )
                        Spacer(Modifier.width(8.dp))
                        Text(
                            text = l10n("Üye Yönetim Paneli"),
                            fontWeight = FontWeight.Bold,
                            fontSize = 18.sp
                        )
                    }
                },
                navigationIcon = {
                    IconButton(onClick = onNavigateBack) {
                        Icon(Icons.AutoMirrored.Filled.ArrowBack, contentDescription = l10n("Geri"))
                    }
                },
                actions = {
                    IconButton(onClick = { viewModel.loadMembers() }) {
                        Icon(Icons.Default.Refresh, contentDescription = l10n("Yenile"))
                    }
                },
                colors = TopAppBarDefaults.topAppBarColors(
                    containerColor = Color.White
                )
            )
        }
    ) { padding ->
        Box(
            modifier = Modifier
                .fillMaxSize()
                .background(Color(0xFFF8FAFC))
                .padding(padding)
        ) {
            when (val s = state) {
                is MemberListUiState.Loading -> {
                    Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                        CircularProgressIndicator(color = Color(0xFF0F172A))
                    }
                }
                is MemberListUiState.Error -> {
                    Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                        Column(horizontalAlignment = Alignment.CenterHorizontally) {
                            Text(
                                text = s.message,
                                color = MaterialTheme.colorScheme.error,
                                style = MaterialTheme.typography.bodyMedium
                            )
                            Spacer(Modifier.height(12.dp))
                            Button(onClick = { viewModel.loadMembers() }) {
                                Text(l10n("Tekrar Dene"))
                            }
                        }
                    }
                }
                is MemberListUiState.Success -> {
                    Column(
                        modifier = Modifier
                            .fillMaxSize()
                            .padding(horizontal = 20.dp, vertical = 16.dp)
                    ) {
                        // 1. Arama Çubuğu
                        OutlinedTextField(
                            value = s.searchQuery,
                            onValueChange = { viewModel.onSearchQueryChange(it) },
                            placeholder = { Text(l10n("İsim, e-posta veya üye kodu ara..."), color = Color(0xFF94A3B8)) },
                            leadingIcon = {
                                Icon(Icons.Default.Search, contentDescription = null, tint = Color(0xFF64748B))
                            },
                            trailingIcon = {
                                if (s.searchQuery.isNotBlank()) {
                                    IconButton(onClick = { viewModel.onSearchQueryChange("") }) {
                                        Icon(Icons.Default.Close, contentDescription = null, tint = Color(0xFF64748B))
                                    }
                                }
                            },
                            singleLine = true,
                            shape = RoundedCornerShape(12.dp),
                            colors = OutlinedTextFieldDefaults.colors(
                                focusedBorderColor = Color(0xFF0F172A),
                                unfocusedBorderColor = Color(0xFFE2E8F0),
                                focusedContainerColor = Color.White,
                                unfocusedContainerColor = Color.White
                            ),
                            modifier = Modifier.fillMaxWidth()
                        )

                        Spacer(Modifier.height(12.dp))

                        // 2. Durum Filtreleme Chipleri
                        Row(
                            horizontalArrangement = Arrangement.spacedBy(8.dp),
                            modifier = Modifier.fillMaxWidth()
                        ) {
                            FilterChip(
                                selected = s.selectedStatus == null,
                                onClick = { viewModel.onFilterStatusChange(null) },
                                label = { Text(l10n("Tümü (${s.members.size})")) }
                            )
                            FilterChip(
                                selected = s.selectedStatus == true,
                                onClick = { viewModel.onFilterStatusChange(true) },
                                label = { Text(l10n("Aktif (${s.members.count { it.is_active }})")) }
                            )
                            FilterChip(
                                selected = s.selectedStatus == false,
                                onClick = { viewModel.onFilterStatusChange(false) },
                                label = { Text(l10n("Pasif / Kısıtlı (${s.members.count { !it.is_active }})")) }
                            )
                        }

                        Spacer(Modifier.height(16.dp))

                        // 3. Üye Listesi
                        val filteredList = s.filteredMembers
                        if (filteredList.isEmpty()) {
                            Box(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .weight(1f),
                                contentAlignment = Alignment.Center
                            ) {
                                Text(
                                    text = l10n("Aramaya uygun üye bulunamadı."),
                                    color = Color(0xFF64748B),
                                    style = MaterialTheme.typography.bodyMedium
                                )
                            }
                        } else {
                            val listState = androidx.compose.foundation.lazy.rememberLazyListState()
                            LazyColumn(
                                state = listState,
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .weight(1f)
                                    .verticalScrollbar(listState),
                                verticalArrangement = Arrangement.spacedBy(10.dp)
                            ) {
                                items(filteredList, key = { it.id }) { member ->
                                    MemberCompactRowItem(
                                        member = member,
                                        onStatusToggle = { viewModel.toggleMemberStatus(member) },
                                        onSaveMember = { updated -> viewModel.updateMemberData(updated) }
                                    )
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
fun MemberCompactRowItem(
    member: Member,
    onStatusToggle: () -> Unit,
    onSaveMember: (Member) -> Unit
) {
    var memberCode by remember(member.member_code) { mutableStateOf(member.member_code ?: "") }
    var startDate by remember(member.start_date) { mutableStateOf(member.start_date ?: "") }
    var endDate by remember(member.end_date) { mutableStateOf(member.end_date ?: "") }
    var hotelName by remember(member.hotel_name) { mutableStateOf(member.hotel_name ?: "") }
    var phone by remember(member.phone) { mutableStateOf(member.phone ?: "") }
    var email by remember(member.email) { mutableStateOf(member.email) }

    val hasChanges = memberCode != (member.member_code ?: "") ||
            startDate != (member.start_date ?: "") ||
            endDate != (member.end_date ?: "") ||
            hotelName != (member.hotel_name ?: "") ||
            phone != (member.phone ?: "") ||
            email != member.email

    val commitChanges = {
        if (hasChanges) {
            onSaveMember(
                member.copy(
                    member_code = memberCode.trim().uppercase(),
                    start_date = startDate.trim().takeIf { it.isNotBlank() },
                    end_date = endDate.trim().takeIf { it.isNotBlank() },
                    hotel_name = hotelName.trim().takeIf { it.isNotBlank() },
                    phone = phone.trim().takeIf { it.isNotBlank() },
                    email = email.trim().lowercase()
                )
            )
        }
    }

    Card(
        shape = RoundedCornerShape(16.dp),
        colors = CardDefaults.cardColors(
            containerColor = if (member.is_active) Color.White else Color(0xFFF8FAFC)
        ),
        elevation = CardDefaults.cardElevation(defaultElevation = if (member.is_active) 1.dp else 0.dp),
        border = BorderStroke(
            1.dp,
            if (hasChanges) Color(0xFFD97706) else if (member.is_active) Color(0xFFE2E8F0) else Color(0xFFCBD5E1)
        ),
        modifier = Modifier.fillMaxWidth()
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 14.dp, vertical = 10.dp)
                .horizontalScroll(rememberScrollState()),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(10.dp)
        ) {
            // 1. Avatar İkonu
            Box(
                modifier = Modifier
                    .size(38.dp)
                    .clip(RoundedCornerShape(10.dp))
                    .background(
                        if (member.is_active) Color(0xFF0F172A).copy(alpha = 0.08f)
                        else Color(0xFFEF4444).copy(alpha = 0.1f)
                    ),
                contentAlignment = Alignment.Center
            ) {
                Icon(
                    imageVector = if (member.role == "ADMIN") Icons.Outlined.AdminPanelSettings else Icons.Outlined.Person,
                    contentDescription = null,
                    tint = if (member.is_active) Color(0xFF0F172A) else Color(0xFFEF4444),
                    modifier = Modifier.size(20.dp)
                )
            }

            // 2. Üyelik Kodu
            CompactFieldColumn(
                label = l10n("Üyelik Kodu"),
                value = memberCode,
                placeholder = "MBR-...",
                width = 130.dp,
                onValueChange = { memberCode = it.uppercase() },
                onFocusLost = commitChanges
            )

            // 3. Üye Başlangıç
            CompactFieldColumn(
                label = l10n("Üye Başlangıç"),
                value = startDate,
                placeholder = "YYYY-AA-GG",
                width = 125.dp,
                onValueChange = { startDate = it },
                onFocusLost = commitChanges
            )

            // 4. Üye Bitiş
            CompactFieldColumn(
                label = l10n("Üye Bitiş"),
                value = endDate,
                placeholder = "YYYY-AA-GG",
                width = 125.dp,
                onValueChange = { endDate = it },
                onFocusLost = commitChanges
            )

            // 5. Otel Adı
            CompactFieldColumn(
                label = l10n("Otel Adı"),
                value = hotelName,
                placeholder = "Otel Adı...",
                width = 150.dp,
                onValueChange = { hotelName = it },
                onFocusLost = commitChanges
            )

            // 6. Telefon
            CompactFieldColumn(
                label = l10n("Telefon"),
                value = phone,
                placeholder = "+90...",
                width = 130.dp,
                onValueChange = { phone = it },
                onFocusLost = commitChanges
            )

            // 7. e-mail
            CompactFieldColumn(
                label = l10n("e-mail"),
                value = email,
                placeholder = "ornek@mail.com",
                width = 200.dp,
                onValueChange = { email = it },
                onFocusLost = commitChanges
            )

            // Değişiklik Varsa Hızlı Kaydet Butonu
            if (hasChanges) {
                IconButton(
                    onClick = commitChanges,
                    modifier = Modifier
                        .size(34.dp)
                        .clip(RoundedCornerShape(8.dp))
                        .background(Color(0xFF10B981))
                ) {
                    Text("✓", color = Color.White, fontWeight = FontWeight.Bold, fontSize = 14.sp)
                }
            }

            Spacer(Modifier.width(4.dp))

            // 8. Aktif / Pasif Switch Bileşeni
            Column(
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.Center,
                modifier = Modifier.padding(start = 4.dp)
            ) {
                Switch(
                    checked = member.is_active,
                    onCheckedChange = { onStatusToggle() },
                    colors = SwitchDefaults.colors(
                        checkedThumbColor = Color.White,
                        checkedTrackColor = Color(0xFF10B981),
                        uncheckedThumbColor = Color.White,
                        uncheckedTrackColor = Color(0xFFEF4444)
                    )
                )
                Text(
                    text = if (member.is_active) l10n("Açık") else l10n("Engelli"),
                    fontSize = 10.sp,
                    color = if (member.is_active) Color(0xFF10B981) else Color(0xFFEF4444),
                    fontWeight = FontWeight.SemiBold
                )
            }
        }
    }
}

@Composable
private fun CompactFieldColumn(
    label: String,
    value: String,
    placeholder: String,
    width: Dp,
    onValueChange: (String) -> Unit,
    onFocusLost: () -> Unit
) {
    Column(
        modifier = Modifier.width(width),
        verticalArrangement = Arrangement.spacedBy(3.dp)
    ) {
        Text(
            text = label,
            fontSize = 11.sp,
            fontWeight = FontWeight.SemiBold,
            color = Color(0xFF475569),
            maxLines = 1
        )
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .height(34.dp)
                .clip(RoundedCornerShape(8.dp))
                .background(Color(0xFFF8FAFC))
                .border(1.dp, Color(0xFFE2E8F0), RoundedCornerShape(8.dp))
                .padding(horizontal = 8.dp),
            contentAlignment = Alignment.CenterStart
        ) {
            if (value.isBlank()) {
                Text(
                    text = placeholder,
                    fontSize = 12.sp,
                    color = Color(0xFF94A3B8),
                    maxLines = 1
                )
            }
            BasicTextField(
                value = value,
                onValueChange = onValueChange,
                singleLine = true,
                textStyle = TextStyle(
                    fontSize = 12.sp,
                    color = Color(0xFF0F172A),
                    fontWeight = FontWeight.Medium
                ),
                modifier = Modifier.fillMaxWidth()
            )
        }
    }
}
